import { Content } from "@r8er/components/marketing/content";
import styles from "@r8er/components/marketing/content/content.module.css";
import {
  CtaBlock,
  Ctas,
  LargeButton,
  SignUpButton,
} from "@r8er/components/marketing/ctas";
import { Hero } from "@r8er/components/marketing/hero";
import { Layout } from "@r8er/components/marketing/layout";
import { Newsletter } from "@r8er/components/marketing/newsletter";
import { getMembershipOptions } from "@r8er/lib/cms";
import { InferGetStaticPropsType } from "next";
import Head from "next/head";

export async function getStaticProps(context) {
  const membershipOptions = await getMembershipOptions();

  return {
    props: {
      membershipOptions,
    },
  };
}

export default function Investors({
  membershipOptions,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const sectionHeight = 750;

  return (
    <div>
      <Head>
        <title>Investors - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <Hero isSlim title="Investors" img="/investors-hero.png" />

        <Content
          isFullWidth
          isNarrow
          className={styles.topSection}
          isCentered
          style={{
            paddingTop: "calc(80px * var(--height-increment))",
            paddingBottom: 0,
            marginBottom: "calc(140px * var(--height-increment))",
          }}
        >
          <p
            className="p--standard"
            style={{
              fontSize: "calc(1.75 * var(--font-size-base))",
              margin: "0 auto",
              letterSpacing: "-.25px",
            }}
          >
            As a registered investor with R8ER, you'll gain exclusive access to
            businesses that have been carefully vetted. R8ER is free for
            investors to join and use!
          </p>
          <SignUpButton text="Join investor waitlist" />
        </Content>

        <Content
          outerImage="/investors-hand.png"
          className={styles.splitSection}
          style={{
            paddingTop: "0",
          }}
        >
          <h2
            className="h2--standard"
            style={{ color: "var(--brand-secondary)", width: "705px" }}
          >
            As an investor, how can R8ER provide me with a market advantage?
          </h2>
          <div style={{ maxWidth: "575px", marginBottom: "7.5rem" }}>
            <p className="p--standard">
              Thanks to its carefully selected advisors, R8ER evaluates business
              ideas and presents them to its investors in an easy-to-digest
              format that highlights the experts' feedback including strengths,
              weaknesses and potential in the market.
            </p>
            <p className="p--standard">
              Each company is also accompanied by a profile and direct contact
              information, meaning investors can connect with entrepreneurs
              directly. Most importantly, on joining, you specify your
              investment preferences across a multitude of metrics, and R8ER
              will only ever show you companies which match these!
            </p>
          </div>
        </Content>

        <Content
          isGrey
          isRightAligned
          backgroundImage="/investors-meeting.png"
          minHeight={sectionHeight}
        >
          <h2 className="h2--standard">
            How does the assessment
            <br /> process work?
          </h2>
          <p className="p--standard">
            After submitting their documentation, all entrepreneurial candidates
            have their businesses reviewed by ten R8ERs; a carefully selected
            panel of top healthcare and investment experts chosen especially for
            each individual company, who thoroughly review each application in
            order to come up with a total score and assessment.
          </p>
          <p className="p--standard">
            Once their feedback and recommendations for improvement are fed back
            to the entrepreneurs, companies are made available to
            R8ER-registered investors.
          </p>
        </Content>

        <Content
          backgroundImage="/investors-seats-2.png"
          minHeight={sectionHeight}
        >
          <h2 className="h2--standard">Who are the R8ERs?</h2>
          <p className="p--standard">
            R8ERs are a hand-selected group of top healthcare professionals who
            include partner-level investors, specialty professors, consultant
            clinicians and policy regulatory experts amongst others.{" "}
            <a style={{ textDecoration: "underline" }} href="/about">
              Click here to see our R8ERs
            </a>
            .
          </p>
        </Content>

        <Content
          isBlue
          isRightAligned
          backgroundImage="/investors-meeting-blue.png"
          minHeight={sectionHeight}
        >
          <h2 className="h2--standard">
            How do the R8ERs conduct their assessments?
          </h2>
          <p className="p--standard">
            By looking at each aspect of an entrepreneurs idea, the R8ERs break
            their assessment into smaller subcategories including business model
            and strategy, product, science and technology, team and advisors,
            and financials and customers.
          </p>
          <p className="p--standard">
            Thanks to the R8ERs aggregated skills which include extensive
            experience across healthcare, each score represents a fair
            cross-section of expertise, meaning investors are given a clear
            picture of the entrepreneurs' current status, strategy moving
            forward and likelihood of success.
          </p>
        </Content>

        <Content
          backgroundImage="/investors-negotiation.png"
          minHeight={sectionHeight}
        >
          <h2 className="h2--standard">
            If I like a particular business, how do I start negotiations?
          </h2>
          <p className="p--standard">
            In order to save time, each business has its contact information
            attached, meaning you are free to contact the designated company
            representative at your convenience. Negotiations take place between
            investor and entrepreneur directly.
          </p>
        </Content>

        <Content
          backgroundImage="/investor-next.png"
          isRightAligned
          style={{ paddingBottom: 0 }}
        >
          <h2 className="h2--standard">What are the membership options?</h2>
          <div
            className="p--standard"
            dangerouslySetInnerHTML={{ __html: membershipOptions.html }}
          />
          <LargeButton text="Join investor waitlist" />
          <LargeButton
            href="https://www.eventbrite.co.uk/e/926935618387?aff=oddtdtcreator"
            text="Request event ticket"
          />
        </Content>

        <Ctas>
          <CtaBlock
            title="Register to join the R8ER investor waitlist"
            titleClasName={styles.wideTitle}
            img="/cta-investors.png"
            ctaItems={[
              { text: "Join investor waitlist", link: "/auth/signup" },
            ]}
            isLight
          />
          <CtaBlock
            title="Speak to a member of the R8ER team"
            img="/cta-contact.png"
            ctaItems={[
              { text: "Contact Us", link: "/contact" },
              {
                text: "Book a demo",
                link: "https://calendly.com/d/ysy-3d8-hpd/r8er-investor-one-to-one-meeting",
              },
            ]}
          />
        </Ctas>

        <Newsletter />
      </Layout>
    </div>
  );
}
